import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, UserData } from '../stores/AuthStore';
import { UserProfile, LoginRequest, RegisterRequest } from '../models/auth';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  isAuthReady: boolean;
  setAuthReady: (ready: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Convert UserData to UserProfile format for backwards compatibility
const convertToUserProfile = (userData: UserData | null): UserProfile | null => {
  if (!userData) return null;
  
  return {
    id: userData.id,
    userId: userData.id,
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    username: userData.username,
    fullName: userData.fullName,
    createdAt: userData.createdAt,
    lastLoginAt: userData.lastLoginAt,
    isActive: userData.isActive,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isAuthenticated, user: storeUser, checkAuth, logout: storeLogout } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Convert store user to UserProfile format
  const user = convertToUserProfile(storeUser);

  useEffect(() => {
    // Initial authentication check
    const checkInitialAuth = async () => {
      try {
        const isAuth = checkAuth();
        console.log('Initial auth check:', isAuth);

        // If we're already authenticated, mark as ready
        if (isAuth) {
          setIsAuthReady(true);

          // Optionally fetch user profile in the background (non-blocking)
          // Only if we have minimal user data from token
          const currentUser = useAuthStore.getState().user;

          if (currentUser && currentUser.id) {
            console.log('User authenticated with token data, will fetch full profile in background');

            // Fetch profile in background, don't block the auth flow
            const { fetchUserProfile } = useAuthStore.getState();
            fetchUserProfile().catch((error) => {
              console.log('Background profile fetch failed (non-critical):', error);
              // Ignore errors - we can work with token data
            });
          }
        }
      } catch (error) {
        console.error('Initial auth check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkInitialAuth();
  }, []); // Remove checkAuth from dependencies to prevent infinite loop

  // SSO Login function (redirects to central auth)
  const login = async (credentials: LoginRequest) => {
    // For SSO, we don't use credentials directly
    // Instead, redirect to central auth app
    const CENTRAL_APP_URL = import.meta.env.VITE_CENTRAL_APP_URL || 'https://beta.creativetoolbox.ai';
    const redirectUrl = encodeURIComponent(window.location.origin + '/sync-session');
    const loginUrl = `${CENTRAL_APP_URL}/login?redirectTo=${redirectUrl}`;

    console.log('SSO Login: Redirecting to', loginUrl);
    window.location.href = loginUrl;
  };

  // Register function (redirects to central auth)
  const register = async (userData: RegisterRequest) => {
    // For SSO, redirect to central auth registration
    const CENTRAL_APP_URL = import.meta.env.VITE_CENTRAL_APP_URL || 'https://beta.creativetoolbox.ai';
    const redirectUrl = encodeURIComponent(window.location.origin + '/sync-session');
    const registerUrl = `${CENTRAL_APP_URL}/register?redirectTo=${redirectUrl}`;

    console.log('SSO Register: Redirecting to', registerUrl);
    window.location.href = registerUrl;
  };

  const logout = async () => {
    try {
      // Clear local auth state
      storeLogout();
      
      // Optionally, you can also notify the central auth app about logout
      // const CENTRAL_APP_URL = import.meta.env.VITE_CENTRAL_APP_URL;
      // window.location.href = `${CENTRAL_APP_URL}/logout`;
      
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const refreshAuth = async () => {
    try {
      const isAuth = checkAuth();
      if (!isAuth) {
        navigate('/login');
      }
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      navigate('/login');
    }
  };

  const setAuthReady = (ready: boolean) => {
    setIsAuthReady(ready);
    setIsLoading(!ready);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshAuth,
    isAuthReady,
    setAuthReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};